import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import {
  FaUser, FaWallet, FaStore, FaVideoSlash, FaSignOutAlt, FaChevronDown, FaTicketAlt,
  FaMusic, FaBook, FaQuestionCircle, FaSearch, FaVideo, FaGraduationCap, FaChalkboardTeacher,
  FaRobot, FaDumbbell, FaWhatsapp, FaComments, FaUserFriends, FaEnvelope
} from 'react-icons/fa';
import ProfileImageUpload from './ProfileImageUpload';
import SearchBar from './SearchBar';

function Header() {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState<number | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const user = getCurrentUser();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    }
  }, [user]);

  const checkSubscriptionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('stripepracticesubscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .gte('valid_until', new Date().toISOString())
        .order('valid_until', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error checking subscription:', error);
        return;
      }

      if (data) {
        setHasSubscription(true);

        // Calculate days left
        const validUntil = new Date(data.valid_until);
        const today = new Date();
        const diffTime = validUntil.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setSubscriptionDaysLeft(diffDays);
      } else {
        setHasSubscription(false);
        setSubscriptionDaysLeft(null);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (user?.id) {
        // mark offline (best effort)
        const { error } = await supabase
          .from('stripeusers')
          .update({ is_online: false })
          .eq('id', user.id);
        if (error) console.warn('Failed to update is_online:', error);
      }

      // fully end the Supabase session (all tabs)
      await supabase.auth.signOut({ scope: 'global' });

      // clear local caches
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');

      // remove any OAuth hash fragments so a refresh can't resurrect a session
      if (
        window.location.hash.includes('access_token') ||
        window.location.hash.includes('refresh_token')
      ) {
        history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }

      setShowProfileMenu(false);

      // small delay to avoid racing auth listeners
      await new Promise((r) => setTimeout(r, 50));

      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error(error?.message || 'Failed to sign out');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleProfileUpdate = (imageUrl: string) => {
    if (user) {
      const updatedUser = { ...user, profile_image: imageUrl };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setShowProfileUpload(false);
      window.location.reload(); // Refresh to update all components
    }
  };

  return (
    <header className="bg-black shadow-sm fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12">
          {/* Logo */}
          <div className="flex items-center h-8">
            <Link to="/" className="flex items-center">
              <img
                src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/amalieaispanishlogo.png"
                alt="Langrigo Learning Platform"
                className="h-8 w-auto animated-logo"
              />
            </Link>
          </div>

          {/* Search Bar - Only show for authenticated users and desktop */}
          {user && (
            <div className="hidden sm:block flex-1 max-w-xs mx-4">
              <SearchBar className="max-w-xs" />
            </div>
          )}

          {/* Navigation */}
          {user ? (
            <nav className="hidden md:flex space-x-8">
              <Link
                to="/dashboard"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/songs"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Songs
              </Link>

            

              <Link
                to="/stories"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Stories
              </Link>

          
            
              <Link
                to="/learners"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Learners
              </Link>
              <Link
                to="/friends"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Friends
              </Link>
            </nav>
          ) : (
            <nav className="hidden md:flex space-x-8">
              <Link
                to="/login"
                className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="bg-yellow-500 text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-400"
              >
                Sign up
              </Link>

            
            </nav>
          )}

          {/* Profile Menu */}
          {user && (
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-3 text-white hover:text-gray-200 focus:outline-none"
              >
                <div className="w-8 h-8 rounded-full bg-yellow-900 flex items-center justify-center overflow-hidden">
                  {user.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-medium">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <FaChevronDown className="w-4 h-4" />
              </button>

              {/* Dropdown Menu */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-80 md:w-48 bg-black rounded-lg shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                  {/* Mobile Search */}
                  <div className="block sm:hidden p-4 border-b border-gray-800">
                    <SearchBar isMobile={true} className="w-full" />
                  </div>

                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/profile');
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-gray-900"
                  >
                    <FaUser className="w-4 h-4 mr-3 text-white" />
                    Profile Settings
                  </button>

                  <Link
                    to="/songs"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaMusic className="w-4 h-4 mr-3 text-white" />
                    Songs
                  </Link>

                  <Link
                    to="/practice"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900 relative"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaDumbbell className="w-4 h-4 mr-3 text-white" />
                    Practice
                    {hasSubscription && (
                      <span className="ml-2 bg-yellow-400 text-black text-xs px-1.5 py-0.5 rounded-full">
                        {subscriptionDaysLeft}d
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/stories"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaBook className="w-4 h-4 mr-3 text-white" />
                    Stories
                  </Link>

                  <Link
                    to="/teacher-chat"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaRobot className="w-4 h-4 mr-3 text-white" />
                    AI Teacher
                  </Link>

                 

                  <Link
                    to="/learners"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaUser className="w-4 h-4 mr-3 text-white" />
                    Learners
                  </Link>

                  <Link
                    to="/friends"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaUserFriends className="w-4 h-4 mr-3 text-white" />
                    Friends
                  </Link>

                  <Link
                    to="/messages"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaEnvelope className="w-4 h-4 mr-3 text-white" />
                    Messages
                  </Link>

                  <Link
                    to="/whatsapp"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaWhatsapp className="w-4 h-4 mr-3 text-white" />
                    WhatsApp
                  </Link>

                  <Link
                    to="/wallet"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaWallet className="w-4 h-4 mr-3 text-white" />
                    Wallet
                  </Link>

                  <Link
                    to="/coupons"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-900"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaTicketAlt className="w-4 h-4 mr-3 text-white" />
                    Coupons
                  </Link>

                  <div className="border-t border-gray-800">
                    <button
                      onClick={handleLogout}
                      disabled={loggingOut}
                      className="flex w-full items-center px-4 py-2 text-sm text-white hover:bg-gray-900 disabled:opacity-50"
                    >
                      <FaSignOutAlt className="w-4 h-4 mr-3 text-white" />
                      {loggingOut ? 'Signing out…' : 'Sign out'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Profile Image Upload Modal */}
      {showProfileUpload && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-black rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Update Profile Picture</h3>
              <button
                onClick={() => setShowProfileUpload(false)}
                className="text-white hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ProfileImageUpload
              userId={user.id}
              currentImage={user.profile_image}
              onImageUpdate={handleProfileUpdate}
            />
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
